import './style.css'
import { useState, useEffect, useRef, useMemo } from 'react'
import Loader from "../Loader"
import { HighchartsReact } from 'highcharts-react-official'
import Highcharts from "highcharts/highstock"
import HighchartsMore from 'highcharts/highcharts-more'
import highchartsExporting from 'highcharts/modules/exporting'
import highchartsExportData from 'highcharts/modules/export-data'
import moment from 'moment'
import GraphFuncs from '../../utils/GraphFuncs'
import GlobalSVG from '../../utils/GlobalSVG'
import Tooltip from '../ToolTip'
import SlideToggle from '../SlideToggle';
import MultipleSelect from '../MultipleSelect'
import API from '../../utils/API'
import UnitToggle from '../UnitToggle'
import { DatePickerComponent } from '@syncfusion/ej2-react-calendars'
import logo from "../../assets/images/logo_full_brand.png"

export default function UtilityBillGraph2(props) {

    highchartsExporting(Highcharts)
    highchartsExportData(Highcharts)

    const [unit, setUnit] = useState('price')
    const [andOr, setAndOr] = useState('and')
    const [displayedUtilities, setDisplayedUtilities] = useState([0, 1, 2])
    const [displayOptions, setDisplayOptions] = useState({ montheui: false, monthsqft: false, temp: false })
    const [displayedTags, setDisplayedTags] = useState([])
    const [timeStep, setTimeStep] = useState("0")
    const [temperatureUnit, setTemperatureUnit] = useState('c')
    const [euiTotalPerMonth, setEuiTotalPerMonth] = useState()
    const [euiTotalPerMonthC, setEuiTotalPerMonthC] = useState()
    const [euiNational, setEuiNational] = useState()
    const [euiNationalC, setEuiNationalC] = useState()
    const [maxEui, setMaxEui] = useState(0)
    const [options, setOptions] = useState()
    const [currentData, setCurrentData] = useState(null)
    const [totals, setTotals] = useState({ price: { 0: 0, 1: 0, 2: 0 }, energy: { 0: 0, 1: 0, 2: 0 }, emissions: { 0: 0, 1: 0, 2: 0 }, consumption: { 0: 0, 1: 0, 2: 0 } })
    const [cTotals, setCTotals] = useState({ price: { 0: 0, 1: 0, 2: 0 }, energy: { 0: 0, 1: 0, 2: 0 }, emissions: { 0: 0, 1: 0, 2: 0 }, consumption: { 0: 0, 1: 0, 2: 0 } })
    const [loading, setLoading] = useState(true)
    const chartRef = useRef(null)
    const [buildingOptions, setBuildingOptions] = useState(false)
    const utilityOptions = [{ idAttr: 0, utility: "Electricity" }, { idAttr: 1, utility: "Natural Gas" }, { idAttr: 2, utility: "Water" }]
    const unitDict = useMemo(() => ({ price: { 0: "$", 1: "$", 2: "$" }, energy: { 0: "GJ", 1: "GJ", 2: "GJ" }, emissions: { 0: "t CO₂e", 1: "t CO₂e", 2: "t CO₂e" }, consumption: { 0: "kWh", 1: "m³", 2: "L" } }), [])

    // --- Pending state (controlled by UI, not yet applied) ---
    const [pendingBuilding, setPendingBuilding] = useState()
    const [pendingCompareBuilding, setPendingCompareBuilding] = useState()
    const [pendingCompareStartEnd, setPendingCompareStartEnd] = useState()
    const [showUpdate, setShowUpdate] = useState(false)

    // --- Applied state (drives the chart useEffect) ---
    const [selectedBuilding, setSelectedBuilding] = useState()
    const [compareBuilding, setCompareBuilding] = useState()
    const [compareStartEnd, setCompareStartEnd] = useState()
    const [compareData, setCompareData] = useState(null)

    const tagOptions = useMemo(() => {
        if (!selectedBuilding) return new Set();

        const tempTagOptions = new Set();
        selectedBuilding.bills
            .flatMap(bill => bill.tags ?? [])
            .forEach(tag => tempTagOptions.add(tag));
        if (compareData && compareData.bills.length) {
            compareData.bills
                .flatMap(bill => bill.tags ?? [])
                .forEach(tag => tempTagOptions.add(tag));
        }
        return Array.from(tempTagOptions);
    }, [selectedBuilding, compareData]);

    const getDateKey = (date, step) => {
        const m = moment(date);
        if (step === "0") {
            return m.format('MMMM YYYY');
        } else if (step === "1") {
            const quarter = Math.floor(m.month() / 3) + 1;
            return `Q${quarter} ${m.format('YYYY')}`;
        } else if (step === "2") {
            return m.format('YYYY');
        }
        return m.format('MMMM YYYY');
    };

    // --- Handlers update PENDING state and show the Update button ---

    const handleSelectedBuilding = (e) => {
        const building = props.buildings[e.target.value]
        setPendingBuilding(building)
        setShowUpdate(true)
    }

    const handleCompareBuilding = (e) => {
        setPendingCompareStartEnd(null)
        setPendingCompareBuilding(e.target.value || null)
        setShowUpdate(true)
    }

    const handleDateChange = (data) => {
        if (!data.value) {
            setPendingCompareStartEnd(null)
            setShowUpdate(true)
            return
        }
        setPendingCompareBuilding(null)
        const startDate = data.value
        let diff = GraphFuncs.getDifferenceInMonths(props.startEnd[0], data.value)
        if (moment(props.startEnd[0]).isBefore(moment(data.value))) diff -= 1
        const endDate = moment(props.startEnd[1]).add(diff, 'months')
        setPendingCompareStartEnd([moment(startDate).format("YYYY-MM-01"), moment(endDate).format("YYYY-MM-01")])
        setShowUpdate(true)
    }

    // --- Apply pending state and fetch compare data ---

    const handleCompare = async () => {
        setShowUpdate(false)

        // Apply pending building selection
        if (pendingBuilding) {
            setSelectedBuilding(pendingBuilding)
            setPendingBuilding(null)
        }

        const effectiveCompareStartEnd = pendingCompareStartEnd
        const effectiveCompareBuilding = pendingCompareBuilding

        // Commit compare selections
        setCompareStartEnd(effectiveCompareStartEnd)
        setCompareBuilding(effectiveCompareBuilding)

        // Fetch compare data if needed
        const buildingToQuery = pendingBuilding || selectedBuilding

        if (effectiveCompareStartEnd) {
            try {
                const cancelToken = API.cancelToken()
                let res = await API.getBuildingUtilityBills(cancelToken, buildingToQuery.buildingInfo.building_id, effectiveCompareStartEnd[0], effectiveCompareStartEnd[1])
                let result = res.data
                let tempCompare = {
                    buildingInfo: result.building,
                    bills: result.bills.map(item => {
                        let date = moment([item.bill_year, item.bill_month - 1, 1]).format("YYYY-MM-DD")
                        let obj = { ...item, date: date, total_cost: item.basic_monthly + item.delivery_charges + item.other_charges + item.carbon_charge + item.kwh_cost + item.kva_cost + item.municipal_tax + item.gst_cost + item.pst_cost }
                        if (item.type === 1) obj = { ...item, date: date, total_cost: item.basic_monthly + item.delivery_charges + item.other_charges + item.carbon_charge + item.gas_cost + item.municipal_tax + item.gst_cost + item.pst_cost }
                        else if (item.type === 2) obj = { ...item, date: date, total_cost: item.basic_monthly + item.delivery_charges + item.other_charges + item.carbon_charge + item.water_cost + item.sewage_cost + item.municipal_tax + item.gst_cost + item.pst_cost }
                        return obj
                    }),
                }
                setCompareData(tempCompare)
            } catch (e) {
                setShowUpdate(true)
                console.log(e)
            }
        } else if (effectiveCompareBuilding) {
            setCompareData(props.buildings[effectiveCompareBuilding])
        } else {
            setCompareData(null)
        }
    }

    // Initialize selected building once buildings load
    useEffect(() => {
        if (!props.buildings) return
        const firstBuilding = Object.values(props.buildings)[0]
        setSelectedBuilding(firstBuilding)
        setPendingBuilding(firstBuilding)
        setBuildingOptions(Object.values(props.buildings).map(e => ({ name: e.buildingInfo.building, buildingId: e.buildingInfo.building_id })))
    }, [props.buildings])

    const reset = () => {
        const firstBuilding = Object.values(props.buildings)[0]
        setCompareBuilding("")
        setPendingCompareBuilding("")
        setPendingCompareStartEnd(null)
        setCompareStartEnd(null)
        setCompareData(null)
        setSelectedBuilding(firstBuilding)
        setPendingBuilding(firstBuilding)
        setBuildingOptions(Object.values(props.buildings).map(e => ({ name: e.buildingInfo.building, buildingId: e.buildingInfo.building_id })))
        setUnit('price')
        setAndOr('and')
        setDisplayedUtilities([0, 1, 2])
        setDisplayOptions({ montheui: false, monthsqft: false, temp: false })
        setDisplayedTags([])
        setTimeStep("0")
        setShowUpdate(false)
    }

    function monthsBetween(startEnd) {
        const start = moment(startEnd[0]).startOf("month")
        const end = moment(startEnd[1]).startOf("month")
        const diff = end.diff(start, "months")
        return diff + 1
    }

    function generateMonthlyDates(date, step) {
        const dates = [];
        let current = moment(date[0]).startOf('month');
        const end = moment(date[1]).endOf('month');

        if (step === "0") {
            while (current.isSameOrBefore(end)) {
                dates.push(current.clone().format("MMMM YYYY"));
                current.add(1, 'month');
            }
        } else if (step === "1") {
            current = current.startOf('quarter');
            while (current.isSameOrBefore(end)) {
                const quarter = Math.floor(current.month() / 3) + 1;
                dates.push(`Q${quarter} ${current.format('YYYY')}`);
                current.add(1, 'quarter');
            }
        } else if (step === "2") {
            current = current.startOf('year');
            while (current.isSameOrBefore(end)) {
                dates.push(current.format('YYYY'));
                current.add(1, 'year');
            }
        }

        return dates;
    }

    function zipLongest(arr1, arr2) {
        const length = Math.max(arr1.length, arr2.length);
        return Array.from({ length }, (_, i) => [arr1[i], arr2[i]]);
    }

    // Main chart data effect — only re-runs when APPLIED state or display options change
    useEffect(() => {
        if (!props.buildings || !selectedBuilding) return
        const annualizeDict = { "0": 12, "1": 4, "2": 1 }
        const calculateTotal = (bills, unit, displayedUtilities, step, divisor = 1) => {
            const conversions = {
                energy: { 0: (item) => item.kwh_consumption / 277.7778, 1: (item) => item.gas_consumption / 26.853 },
                emissions: { 0: (item) => item.emissions, 1: (item) => item.emissions },
                price: { 0: (item) => item.total_cost, 1: (item) => item.total_cost, 2: (item) => item.total_cost },
                consumption: { 0: (item) => item.kwh_consumption, 1: (item) => item.gas_consumption, 2: (item) => item.water_consumption },
            }
            
            return bills.reduce((total, item) => {
                if (!displayedUtilities.includes(item.type) || (unit === 'emissions' && displayedUtilities === 2)) return total
                let date = getDateKey(item.date, step);
                const value = conversions[unit][item.type]?.(item) ?? 0
                total[date] = total[date] || 0
                total[date] += (value / divisor)
                return total
            }, {})
        }

        const aggregateAccounts = (bills, unit, type, divisor, isSingleUtility, step) => {
            const accountsDict = bills.reduce((accounts, item) => {
                if (item.type !== type || (unit === 'emissions' && type === 2)) return accounts;

                let value
                switch (unit) {
                    case 'energy':
                        if (type === 0) value = item.kwh_consumption / 277.7778
                        else value = item.gas_consumption / 26.853
                        break

                    case 'emissions':
                        value = item.emissions
                        break

                    case 'consumption':
                        if (type === 0) value = item.kwh_consumption
                        else if (type === 1) value = item.gas_consumption
                        else value = item.water_consumption
                        break

                    default:
                        value = item.total_cost
                }

                let date = getDateKey(item.date, step);
                if (isSingleUtility && unit === 'price') {
                    let acc
                    switch (type) {
                        case 0: default:
                            acc = accounts[item.account_name] ?? {};
                            acc[date] = acc[date] || {
                                total: 0,
                                basic: 0,
                                kva: 0,
                                kwh: 0,
                                carbon_charge: 0,
                                taxes: 0,
                                delivery: 0,
                                other: 0
                            }
                            acc[date].total += value / divisor
                            acc[date].basic += item.basic_monthly / divisor
                            acc[date].kva += item.kva_cost / divisor
                            acc[date].kwh += item.kwh_cost / divisor
                            acc[date].delivery += item.delivery_charges / divisor
                            acc[date].other += item.other_charges / divisor
                            acc[date].carbon_charge += item.carbon_charge / divisor
                            acc[date].taxes += (item.municipal_tax + item.gst_cost + item.pst_cost) / divisor
                            break

                        case 1:
                            acc = accounts[item.account_name] ?? {};
                            acc[date] = acc[date] || {
                                total: 0, basic: 0, gas: 0, carbon_charge: 0, taxes: 0, delivery: 0, other: 0
                            }
                            acc[date].total += value / divisor
                            acc[date].basic += item.basic_monthly / divisor
                            acc[date].gas += item.gas_cost / divisor
                            acc[date].delivery += item.delivery_charges / divisor
                            acc[date].other += item.other_charges / divisor
                            acc[date].carbon_charge += item.carbon_charge / divisor
                            acc[date].taxes += (item.municipal_tax + item.gst_cost + item.pst_cost) / divisor
                            break

                        case 2:
                            acc = accounts[item.account_name] ?? {};
                            acc[date] = acc[date] || {
                                total: 0, basic: 0, water: 0, sewage: 0, carbon_charge: 0, taxes: 0, delivery: 0, other: 0
                            }
                            acc[date].total += value / divisor
                            acc[date].basic += item.basic_monthly / divisor
                            acc[date].water += item.water_cost / divisor
                            acc[date].sewage += item.sewage_cost / divisor
                            acc[date].delivery += item.delivery_charges / divisor
                            acc[date].other += item.other_charges / divisor
                            acc[date].taxes += (item.municipal_tax + item.gst_cost + item.pst_cost) / divisor
                            break


                    }
                    accounts[item.account_name] = acc;
                } else {
                    accounts[item.account_name] = accounts[item.account_name] || {};
                    accounts[item.account_name][date] = accounts[item.account_name][date] || 0;
                    accounts[item.account_name][date] += value / divisor;
                }

                return accounts;
            }, {})

            return Object.entries(accountsDict).reduce((finalDict, [accountName, data], ix) => {
                let dateDict = {}
                const typDict = { 0: "Electrical Bill", 1: "Gas Bill", 2: "Water Bill" }

                Object.keys(data).forEach((date) => {
                    dateDict[date] = {
                        name: `${typDict[type]} ${ix + 1}`,
                        value: { name: date, accountName, y: isSingleUtility && unit === 'price' ? data[date].total : data[date], ...data[date] },
                    }
                }, {})
                finalDict[`${typDict[type]} ${ix + 1}`] = dateDict
                return finalDict
            }, {})
        }

        const cancelToken = API.cancelToken();
        (async () => {
            setLoading(true)
            let temperature
            const getTemp = async (date, building, compare = false) => {
                try {
                    let end = date[1]
                    if (moment(end) > moment()) end = moment().format('YYYY-MM-DD')
                    let res = await API.getTemperature(cancelToken, building, 'daily', date[0], end, temperatureUnit)
                    const tempsByMonth = {};

                    for (const [dateStr, values] of Object.entries(res.data.readings)) {
                        const monthKey = moment(dateStr).format("MMMM YYYY") + (compare ? " (C)" : "")
                        tempsByMonth[monthKey] = tempsByMonth[monthKey] || []
                        tempsByMonth[monthKey].push(values.temp);
                    }

                    const series = [];
                    for (const [month, temps] of Object.entries(tempsByMonth)) {
                        const sum = temps.reduce((a, b) => a + b, 0);
                        let avg = sum / temps.length;
                        series.push({ name: month, y: avg, unit: temperatureUnit })
                    }
                    return series
                } catch (e) {
                    if (e.message === 'cancelling') return
                    console.log(e);
                }
            }
            if (displayOptions.temp) {
                let temp = await getTemp(props.startEnd, selectedBuilding.buildingInfo.building_id)
                let cTemp = null
                if (compareStartEnd) cTemp = await getTemp(compareStartEnd, selectedBuilding.buildingInfo.building_id, true)
                else if (compareData) cTemp = await getTemp(props.startEnd, compareData.buildingInfo.building_id, true)
                if (cTemp) temp = [temp, cTemp]
                else temp = [temp]
                temperature = temp
            }

            let colors = ["#a3d237", "#4fa78b", "#006184", "#6e2b68", "#ffcf00", "#ff7500", "#db0661", "#007b4e", "#588C9E", "#007b4e"]
            const namesDict = { 0: 'Electrical', 1: 'Natural Gas', 2: 'Water' }
            const tempTotal = { price: { 0: 0, 1: 0, 2: 0 }, energy: { 0: 0, 1: 0, 2: 0 }, emissions: { 0: 0, 1: 0, 2: 0 }, consumption: { 0: 0, 1: 0, 2: 0 } }
            const cTempTotal = { price: { 0: 0, 1: 0, 2: 0 }, energy: { 0: 0, 1: 0, 2: 0 }, emissions: { 0: 0, 1: 0, 2: 0 }, consumption: { 0: 0, 1: 0, 2: 0 } }
            const tempSeries = {}

            const building = selectedBuilding;
            const firstDates = generateMonthlyDates(props.startEnd, timeStep)
            const cDates = compareStartEnd ? generateMonthlyDates(compareStartEnd, timeStep) : [...firstDates]
            const dateZip = zipLongest(firstDates, cDates)

            let bills
            if (displayedTags.length) {
                bills = building.bills.filter(bill =>
                    andOr === 'and'
                        ? displayedTags.every(tag => bill.tags.includes(tag))
                        : displayedTags.some(tag => bill.tags.includes(tag))
                );
            } else {
                bills = building.bills
            }

            const compareBills = compareData ? compareData.bills.filter(bill =>
                displayedTags.every(tag => bill.tags.includes(tag))
            ) : []

            const divisor = displayOptions.monthsqft ? building.buildingInfo.sq_footage * 0.092903 : 1
            if (displayedUtilities.length === 1) {
                const type = displayedUtilities[0]
                const accounts = aggregateAccounts(Object.values(bills), unit, type, divisor, true, timeStep)
                const cAccounts = compareData ? aggregateAccounts(Object.values(compareBills), unit, type, divisor, true, timeStep) : []
                const accountsSet = new Set([...Object.keys(accounts), ...Object.keys(cAccounts)])
                accountsSet.forEach((account, ix) => {
                    dateZip.forEach((dateArr) => {
                        tempSeries[ix] = tempSeries[ix] || { name: account, values: [], type: 'column', axis: 0 }
                        tempSeries[ix].values.push({
                            ...accounts?.[ix]?.[dateArr[0]]?.value ?? { name: dateArr[0], y: null },
                            ...Object.fromEntries(
                                Object.entries(cAccounts?.[ix]?.[dateArr[1]]?.value ?? {}).map(([k, v]) => [`${k}_c`, v]))
                        })
                        if (compareData) {
                            tempSeries[ix] = tempSeries[ix] || { name: cAccounts + " (C)", values: [], type: 'column', axis: 0 }
                            tempSeries[ix].values.push({
                                ...cAccounts?.[ix]?.[dateArr[1]]?.value, name: dateArr[1] + " (C)",
                                ...Object.fromEntries(
                                    Object.entries(accounts?.[ix]?.[dateArr[0]]?.value ?? {}).map(([k, v]) => [`${k}_c`, v]))
                            })
                            tempSeries[ix].values.push({ name: dateArr[1] + '!@#$DFWEG%T', y: null })
                        }
                    })
                })
                tempTotal[unit][type] += Object.values(calculateTotal(Object.values(bills), unit, [type], timeStep, divisor)).reduce((sum, item) => sum += item, 0)
                if (compareData) cTempTotal[unit][type] += Object.values(calculateTotal(Object.values(compareBills), unit, [type], timeStep, divisor)).reduce((sum, item) => sum += item, 0)
            } else {
                displayedUtilities.forEach((type) => {
                    const total = calculateTotal(Object.values(bills), unit, [type], timeStep, divisor)
                    const cTotal = compareData ? calculateTotal(Object.values(compareBills), unit, [type], timeStep, divisor) : {}
                    const totalAmount = Object.values(calculateTotal(Object.values(bills), unit, [type], timeStep, divisor)).reduce((sum, item) => sum += item, 0)
                    const cTotalAmount = compareData ? Object.values(calculateTotal(Object.values(compareBills), unit, [type], timeStep, divisor)).reduce((sum, item) => sum += item, 0) : null
                    dateZip.forEach((dateArr) => {
                        tempSeries[type] = tempSeries[type] || { name: namesDict[type], values: [], type: 'column', axis: 0 }
                        tempSeries[type].values.push({ name: dateArr[0], y: total?.[dateArr[0]], y_c: cTotal?.[dateArr[1]] ? cTotal?.[dateArr[1]] : null })
                        if (compareData) {
                            tempSeries[type] = tempSeries[type] || { name: namesDict[type] + " (C)", values: [], type: 'column', axis: 0 }
                            tempSeries[type].values.push({ name: dateArr[1] + " (C)", y: cTotal?.[dateArr[1]], y_c: total?.[dateArr[0]] ? total?.[dateArr[0]] : null })
                            tempSeries[type].values.push({ name: dateArr[1] + '!@#$DFWEG%T', y: null })
                        }
                    })

                    tempTotal[unit][type] += totalAmount
                    if (compareData) cTempTotal[unit][type] += cTotalAmount
                })
            }

            if (building.buildingInfo.sq_footage && displayOptions.montheui) {
                let euiTotals = calculateTotal(Object.values(bills), 'energy', [0, 1], timeStep, building.buildingInfo.sq_footage * 0.092903)
                let cEuiTotals = compareData ? calculateTotal(Object.values(compareBills), 'energy', [0, 1], timeStep, compareData.buildingInfo.sq_footage * 0.092903) : {}

                const euiValues = bills.filter(bill => typeof bill.eui === 'number')
                const euiDict = euiValues.length > 0 ? euiValues.reduce((sumDict, bill) => {
                    let date = moment(bill.date).format('MMMM YYYY')
                    sumDict[date] = sumDict[date] || 0
                    sumDict[date] = bill.eui
                    return sumDict
                }, {}) : {}

                const cEuiValues = compareData ? compareBills.filter(bill => typeof bill.eui === 'number') : {}
                const cEuiDict = cEuiValues.length > 0 ? cEuiValues.reduce((sumDict, bill) => {
                    let date = moment(bill.date).format('MMMM YYYY')
                    sumDict[date] = sumDict[date] || 0
                    sumDict[date] = bill.eui
                    return sumDict
                }, {}) : {}

                dateZip.forEach((dateArr) => {
                    tempSeries["eui"] = tempSeries["eui"] || { name: "EUI Actual (Annualized)", values: [], type: 'line', axis: 1 }
                    tempSeries["eui"].values.push({ name: dateArr[0], y: euiTotals[dateArr[0]] * annualizeDict[timeStep] })
                    if (compareData) {
                        tempSeries["eui"] = tempSeries["eui"] || { name: "EUI Actual (Annualized) (C)", values: [], type: 'line', axis: 0 }
                        tempSeries["eui"].values.push({ name: dateArr[1] + " (C)", y: cEuiTotals[dateArr[1]] * annualizeDict[timeStep] })
                    }
                })

                setMaxEui(Math.max(...[...Object.values(euiTotals), ...Object.values(cEuiTotals)]) * annualizeDict[timeStep])
                setEuiTotalPerMonth(Object.values(euiTotals).reduce((sum, item) => sum += item, 0) * annualizeDict[timeStep] / Object.values(euiDict).length)
                setEuiTotalPerMonthC(Object.values(cEuiTotals).reduce((sum, item) => sum += item, 0) * annualizeDict[timeStep] / Object.values(euiDict).length)
                setEuiNational(Object.values(euiDict).reduce((sum, item) => sum += item, 0) / Object.values(euiDict).length)
                setEuiNationalC(Object.values(cEuiDict).reduce((sum, item) => sum += item, 0) / Object.values(cEuiDict).length)
            }

            if (displayOptions.temp && temperature) {
                tempSeries['temperature'] = { name: "Temperature", values: temperature[0], type: 'line', axis: 2 }
                if (temperature.length === 2) tempSeries['temperature (c)'] = { name: "Temperature (C)", values: temperature[1], type: 'line', axis: 2 }
            }

            const tempData = Object.values(tempSeries).map((series, ix) => ({
                name: series.name,
                type: series.type,
                data: series.values,
                color: series.name.includes('Temperature') ? "#85929E " : colors[ix],
                yAxis: series.axis,
                lineWidth: series.name.includes('Temperature') ? 1 : 0,
                marker: {
                    symbol: series.name.includes('Temperature') ? "dot " : "triangle",
                    lineWidth: series.name.includes('Temperature') ? 2 : 4,
                    lineColor: series.name.includes('Temperature') ? "#85929E " : colors[ix],
                }
            })) || [{
                name: '',
                data: [],
                color: colors[0],
                yAxis: 0,
            }]

            setTotals(tempTotal)
            setCTotals(cTempTotal)
            setCurrentData(tempData)
        })();
        return () => {
            API.cancel(cancelToken);
        }
    }, [props.buildings, unit, displayedUtilities, displayOptions, selectedBuilding, props.startEnd, compareData, compareStartEnd, displayedTags, temperatureUnit, timeStep, andOr])


    useEffect(() => {
        if (typeof Highcharts === 'object') {
            HighchartsMore(Highcharts);
        }
        setOptions({
            credits: {
                enabled: false,
            },
            chart: {
                height: '50%',
                marginTop: 100,
            },
            legend: {
                enabled: true,
                align: 'left',
                layout: 'horizontal',
                verticalAlign: 'bottom',
                itemMarginBottom: 0,
                itemMarginTop: 0,
            },
            title: {
                text: '',
                align: 'left'
            },
            subtitle: {
                text: '',
                align: 'left'
            },
            xAxis: {
                type: 'category',
                min: 0,
                max: currentData && currentData[0]?.data.length < 20 ? currentData[0].data.length - 1 : 20,
                accessibility: {
                    description: 'Date'
                },
                scrollbar: {
                    enabled: true
                },
                labels: {
                    enabled: true,
                    formatter: function (e) {
                        return `${e.value}`.includes("!@#$DFWEG%T") ? "" : e.value
                    },
                },
            },
            yAxis: [{
                title: {
                    align: 'high',
                    offset: 15,
                    rotation: 0,
                    y: -15,
                    text: `${unitDict[unit][displayedUtilities[0]]}${displayOptions.monthsqft ? "/m²" : ""}`
                },
                labels: {
                    formatter: function () {
                        return this.value
                    }
                },
            },
            {
                title: {
                    align: 'high',
                    offset: 15,
                    rotation: 0,
                    y: -15,
                    text: 'EUI'
                },
                visible: currentData && currentData.filter(e => e.name.includes('EUI Actual')).length > 0,
                labels: {
                    formatter: function () {
                        return this.value
                    }
                },
                opposite: true,
                min: 0,
                max: Math.ceil(maxEui),
                plotLines: [
                    displayOptions.montheui && euiTotalPerMonth && {
                        value: euiTotalPerMonth,
                        color: 'orange',
                        width: 1,
                        dashStyle: 'Dash',
                        label: {
                            text: 'EUI (Actual)',
                            align: 'right',
                            style: { color: 'orange', fontWeight: 'bold' }
                        },
                        zIndex: 5
                    },
                    displayOptions.montheui && euiNational && {
                        value: euiNational,
                        color: 'green',
                        width: 1,
                        dashStyle: 'Dash',
                        label: {
                            text: 'EUI (National)',
                            align: 'right',
                            style: { color: 'green', fontWeight: 'bold' }
                        },
                        zIndex: 5
                    },
                    displayOptions.montheui && euiTotalPerMonthC && {
                        value: euiTotalPerMonthC,
                        color: 'orange',
                        width: 1,
                        dashStyle: 'Dash',
                        label: {
                            text: 'EUI (Actual) (C)',
                            align: 'right',
                            style: { color: 'orange', fontWeight: 'bold' }
                        },
                        zIndex: 5
                    },
                    displayOptions.montheui && euiNationalC && {
                        value: euiNationalC,
                        color: 'green',
                        width: 1,
                        dashStyle: 'Dash',
                        label: {
                            text: 'EUI (National) (C)',
                            align: 'right',
                            style: { color: 'green', fontWeight: 'bold' }
                        },
                        zIndex: 5
                    },
                ]
            },
            {
                title: {
                    align: 'high',
                    offset: 15,
                    rotation: 0,
                    y: -15,
                    text: temperatureUnit === 'c' ? "°C" : "°F"
                },
                visible: displayOptions.temp,
                labels: {
                    formatter: function () {
                        return this.value
                    }
                },
                opposite: true,
            }],
            tooltip: {
                formatter: function () {
                    const formatValue = (value, showCurrency = false) => {
                        const options = displayOptions.monthsqft
                            ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                            : { maximumFractionDigits: 0 };
                        const formatted = value.toLocaleString(undefined, options);
                        const currencyPrefix = (showCurrency && unit === 'price') ? unitDict[unit][displayedUtilities[0]] : '';
                        const unitSuffix = (unit !== 'price') ? unitDict[unit][displayedUtilities[0]] : '';
                        const sqftSuffix = displayOptions.monthsqft ? '/m²' : '';
                        return `${currencyPrefix}${formatted}${unitSuffix}${sqftSuffix}`;
                    };

                    const useSqft = displayOptions.monthsqft;
                    const formatNumber = (value, forceDecimals = false) => {
                        const options = (useSqft || forceDecimals)
                            ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                            : { maximumFractionDigits: 0 };
                        return value.toLocaleString(undefined, options);
                    };

                    const addLine = (label, value, showCurrency = false, compare = null) => {
                        let percentage = null
                        if (compare) percentage = (value - compare) / compare
                        return `<b>${label}: ${formatValue(value, showCurrency)}${percentage !== null ? " (" + (percentage >= 0 ? '+' : '') + formatNumber(percentage * 100, true) + "%)" : ''}</b><br/>`;
                    };

                    if (this.point.accountName) {
                        let str = addLine(this.point.accountName, this.y, true, this.point.y_c && ((this.point.y - this.point.y_c) / this.point.y_c) * 100);
                        if (displayedUtilities.length === 1 && unit === 'price') {
                            let charges = [
                                { label: 'Basic Monthly', value: this.point.basic, percentage: this.point.basic_c ? ((this.point.basic - this.point.basic_c) / this.point.basic_c) * 100 : null },
                                { label: 'Delivery Charges', value: this.point.delivery, percentage: this.point.delivery_c ? ((this.point.delivery - this.point.delivery_c) / this.point.delivery_c) * 100 : null },
                                { label: 'Other Charges', value: this.point.other, percentage: this.point.other_c ? ((this.point.other - this.point.other_c) / this.point.other_c) * 100 : null },
                                { label: 'Carbon Charge', value: this.point.carbon_charge, percentage: this.point.carbon_charge_c ? ((this.point.carbon_charge - this.point.carbon_charge_c) / this.point.carbon_charge_c) * 100 : null },
                                { label: 'Taxes', value: this.point.taxes, percentage: this.point.taxes_c ? ((this.point.taxes - this.point.taxes_c) / this.point.taxes_c) * 100 : null }
                            ];
                            let extraCharges = []
                            const insertIndex = charges.findIndex(c => c.label === 'Basic Monthly') + 1;
                            switch (displayedUtilities[0]) {
                                case 0:
                                    extraCharges = [
                                        { label: 'kVA', value: this.point.kva, percentage: this.point.kva_c ? ((this.point.kva - this.point.kva_c) / this.point.kva_c) * 100 : null },
                                        { label: 'kWh', value: this.point.kwh, percentage: this.point.kwh_c ? ((this.point.kwh - this.point.kwh_c) / this.point.kwh_c) * 100 : null },
                                    ];
                                    break
                                case 1:
                                    extraCharges = [
                                        { label: 'Gas Cost', value: this.point.gas, percentage: this.point.gas_c ? ((this.point.gas - this.point.gas_c) / this.point.gas_c) * 100 : null },
                                    ];
                                    break
                                case 2:
                                    extraCharges = [
                                        { label: 'Water Cost', value: this.point.water, percentage: this.point.water_c ? ((this.point.water - this.point.water_c) / this.point.water_c) * 100 : null },
                                        { label: 'Sewage Cost', value: this.point.sewage, percentage: this.point.sewage_c ? ((this.point.sewage - this.point.sewage_c) / this.point.sewage_c) * 100 : null },
                                    ];
                                    break
                                default:
                                    extraCharges = []
                                    break
                            }
                            charges.splice(insertIndex, 0, ...extraCharges)
                            charges.forEach(({ label, value, percentage }) => {
                                str += `<b>${label}: $${formatNumber(value)}${useSqft ? '/m²' : ''}${percentage !== null ? " (" + (percentage >= 0 ? '+' : '') + formatNumber(percentage, true) + "%)" : ''}</b><br/>`;
                            });
                            return str
                        }
                    }

                    if (this.series.name.includes('EUI Actual')) {
                        return `<b>${this.series.name}: ${(+this.y.toFixed(3)).toLocaleString()} GJ/m²/Year</b><br/>`;
                    }

                    if (this.series.name.includes('Benchmark EUI')) {
                        const value = this.y === 0 ? 'N/A' : (+this.y.toFixed(3)).toLocaleString();
                        return `<b>${this.series.name}: ${value} GJ/m²/Year</b><br/>`;
                    }

                    if (this.series.name.includes('Temperature')) {
                        return `<b>${this.series.name}: ${Math.round(this.y).toLocaleString()}°${temperatureUnit.toUpperCase()}</b><br/>`;
                    }
                    return addLine(this.series.name, this.y, unit === 'price', compareData && this.point.options.y_c);
                }
            },
            plotOptions: {
                column: {
                    dataLabels: { enabled: false }
                },
                series: {
                    stacking: 'normal',
                },
            },
            series: currentData ?? [],
            exporting: {
                enabled: true,
                buttons: {
                    contextButton: {
                        menuItems: ['viewFullscreen', 'separator', 'downloadCSV', 'downloadPNG', 'downloadPDF']
                    },
                },
                sourceWidth: 1500,
                sourceHeight: 900,
                allowHTML: true,
                chartOptions: {
                    chart: {
                        marginTop: 250,
                        marginBottom: 200,
                        events: {
                            load: function () {
                                const chart = this;
                                const W = chart.chartWidth;
                                const H = chart.chartHeight;

                                // ── HEADER: white base ──
                                chart.renderer.rect(0, 0, W, 165)
                                    .attr({ fill: '#ffffff', zIndex: 4 })
                                    .add();

                                // ── HEADER: bold left accent bar ──
                                chart.renderer.rect(0, 0, 6, 165)
                                    .attr({ fill: '#007b4e', zIndex: 6 })
                                    .add();

                                // ── HEADER: diagonal decorative shapes (top-right corner) ──
                                chart.renderer.path([
                                    'M', W - 220, 0,
                                    'L', W, 0,
                                    'L', W, 165,
                                    'L', W - 80, 165,
                                    'Z'
                                ])
                                    .attr({ fill: '#007b4e', opacity: 0.08, zIndex: 5 })
                                    .add();

                                chart.renderer.path([
                                    'M', W - 160, 0,
                                    'L', W, 0,
                                    'L', W, 165,
                                    'L', W - 30, 165,
                                    'Z'
                                ])
                                    .attr({ fill: '#a3d237', opacity: 0.13, zIndex: 5 })
                                    .add();

                                // ── HEADER: bottom border lines ──
                                chart.renderer.rect(0, 165, W, 2)
                                    .attr({ fill: '#007b4e', zIndex: 6 })
                                    .add();

                                // ── LOGO ──
                                chart.renderer.image(logo, 22, 18, 210)
                                    .attr({ zIndex: 7 })
                                    .add();

                                // ── REPORT TITLE ──
                                chart.renderer.text('Building Analysis Report', 20, 90)
                                    .attr({ align: 'left', zIndex: 7 })
                                    .css({
                                        color: '#1a1a1a',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        letterSpacing: '0.12em',
                                        textTransform: 'uppercase',
                                    })
                                    .add();

                                // ── BUILDING NAME ──
                                const buildingName = selectedBuilding?.buildingInfo?.building || '';
                                chart.renderer.text(buildingName, 22, 110)
                                    .attr({ align: 'left', zIndex: 7 })
                                    .css({ color: '#007b4e', fontSize: '12px', fontWeight: '600' })
                                    .add();

                                // ── DATE RANGE ──
                                const dateRangeText = props.startEnd
                                    ? `${moment(props.startEnd[0]).format("MMM DD, YYYY")}  →  ${moment(props.startEnd[1]).format("MMM DD, YYYY")}   ·   ${GraphFuncs.getDifferenceInDays(props.startEnd[0], props.startEnd[1])} days`
                                    : '';
                                chart.renderer.text(dateRangeText, 22, 128)
                                    .attr({ align: 'left', zIndex: 7 })
                                    .css({ color: '#999999', fontSize: '11px' })
                                    .add();

                                // ── COMPARE RANGE (if applicable) ──
                                if (compareStartEnd) {
                                    const compareRangeText = `Comparing  ${moment(compareStartEnd[0]).format("MMM DD, YYYY")}  →  ${moment(compareStartEnd[1]).format("MMM DD, YYYY")}`;
                                    chart.renderer.text(compareRangeText, 22, 148)
                                        .attr({ align: 'left', zIndex: 7 })
                                        .css({ color: '#4fa78b', fontSize: '11px' })
                                        .add();
                                } else if (compareData) {
                                    const compareBuildingName = compareData.buildingInfo?.building || '';
                                    chart.renderer.text(`Comparing with  ${compareBuildingName}`, 22, 156)
                                        .attr({ align: 'left', zIndex: 7 })
                                        .css({ color: '#4fa78b', fontSize: '11px' })
                                        .add();
                                }

                                // ── TOTALS: setup ──
                                const prefix = unit === 'price' ? unitDict[unit][displayedUtilities[0]] : '';
                                const suffix = unit !== 'price' ? ` ${unitDict[unit][displayedUtilities[0]]}` : '';
                                const sqftSuffix = displayOptions.monthsqft ? ' /m²' : '';
                                const fmtOpts = displayOptions.monthsqft
                                    ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                    : { maximumFractionDigits: 0 };

                                const grandTotal = displayedUtilities
                                    .reduce((sum, type) => sum + (totals[unit][type] || 0), 0)
                                    .toLocaleString(undefined, fmtOpts);

                                const cGrandTotal = compareData
                                    ? displayedUtilities
                                        .reduce((sum, type) => sum + (cTotals[unit][type] || 0), 0)
                                        .toLocaleString(undefined, fmtOpts)
                                    : null;

                                // Build primary totals cards
                                const totalsItems = [
                                    {
                                        label: 'Grand Total',
                                        value: `${prefix}${grandTotal}${suffix}${sqftSuffix}`,
                                        compareValue: cGrandTotal ? `${prefix}${cGrandTotal}${suffix}${sqftSuffix}` : null,
                                        accent: '#007b4e',
                                        highlight: true,
                                    },
                                ];

                                if (!['eui', 'consumption'].includes(unit)) {
                                    totalsItems.push({
                                        label: 'Electrical',
                                        value: `${prefix}${(totals[unit][0] || 0).toLocaleString(undefined, fmtOpts)}${suffix}${sqftSuffix}`,
                                        compareValue: compareData ? `${prefix}${(cTotals[unit][0] || 0).toLocaleString(undefined, fmtOpts)}${suffix}${sqftSuffix}` : null,
                                        accent: '#a3d237',
                                    });
                                    totalsItems.push({
                                        label: 'Natural Gas',
                                        value: `${prefix}${(totals[unit][1] || 0).toLocaleString(undefined, fmtOpts)}${suffix}${sqftSuffix}`,
                                        compareValue: compareData ? `${prefix}${(cTotals[unit][1] || 0).toLocaleString(undefined, fmtOpts)}${suffix}${sqftSuffix}` : null,
                                        accent: '#4fa78b',
                                    });
                                }

                                if (!['eui', 'energy', 'emissions', 'consumption'].includes(unit)) {
                                    totalsItems.push({
                                        label: 'Water',
                                        value: `${prefix}${(totals[unit][2] || 0).toLocaleString(undefined, fmtOpts)}${suffix}${sqftSuffix}`,
                                        compareValue: compareData ? `${prefix}${(cTotals[unit][2] || 0).toLocaleString(undefined, fmtOpts)}${suffix}${sqftSuffix}` : null,
                                        accent: '#006184',
                                    });
                                }

                                // ── TOTALS: render cards ──
                                // Each card is taller when compare data exists to fit both values
                                const hasCompare = !!compareData;
                                const cardH = hasCompare ? 150 : 100;
                                const blockW = 220;
                                const blockGap = 16;
                                const totalBlocksWidth = totalsItems.length * blockW + (totalsItems.length - 1) * blockGap;
                                const startX = W - totalBlocksWidth - 30;

                                totalsItems.forEach((item, i) => {
                                    const x = startX + i * (blockW + blockGap);
                                    const cardTop = 14;

                                    // card background
                                    chart.renderer.rect(x, cardTop, blockW, cardH, 3)
                                        .attr({ fill: item.highlight ? '#007b4e' : '#f7f7f7', zIndex: 6 })
                                        .add();

                                    // top accent bar
                                    chart.renderer.rect(x, cardTop, blockW, 3, 0)
                                        .attr({ fill: item.accent, zIndex: 7 })
                                        .add();

                                    // label
                                    chart.renderer.text(item.label.toUpperCase(), x + 14, cardTop + 24)
                                        .attr({ zIndex: 8 })
                                        .css({
                                            color: item.highlight ? 'rgba(255,255,255,0.75)' : '#aaaaaa',
                                            fontSize: '9px',
                                            fontWeight: '700',
                                            letterSpacing: '0.1em',
                                        })
                                        .add();

                                    // primary value
                                    chart.renderer.text(item.value, x + 14, cardTop + 60)
                                        .attr({ zIndex: 8 })
                                        .css({
                                            color: item.highlight ? '#ffffff' : '#1a1a1a',
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                        })
                                        .add();

                                    // compare value (if exists)
                                    if (hasCompare && item.compareValue) {
                                        // subtle divider
                                        chart.renderer.rect(x + 14, cardTop + 72, blockW - 28, 1)
                                            .attr({ fill: item.highlight ? 'rgba(255,255,255,0.2)' : '#e0e0e0', zIndex: 8 })
                                            .add();

                                        // (C) label
                                        chart.renderer.text('COMPARISON', x + 14, cardTop + 92)
                                            .attr({ zIndex: 8 })
                                            .css({
                                                color: item.highlight ? 'rgba(255,255,255,0.55)' : '#bbbbbb',
                                                fontSize: '8px',
                                                fontWeight: '700',
                                                letterSpacing: '0.1em',
                                            })
                                            .add();

                                        // compare value
                                        chart.renderer.text(item.compareValue, x + 14, cardTop + 128)
                                            .attr({ zIndex: 8 })
                                            .css({
                                                color: item.highlight ? 'rgba(255,255,255,0.85)' : '#4fa78b',
                                                fontSize: '18px',
                                                fontWeight: 'bold',
                                            })
                                            .add();
                                    }
                                });

                                // ── FOOTER ──
                                chart.renderer.rect(0, H - 58, W, 58)
                                    .attr({ fill: '#f9f9f9', zIndex: 4 })
                                    .add();

                                chart.renderer.rect(0, H - 60, W, 3)
                                    .attr({ fill: '#007b4e', zIndex: 5 })
                                    .add();
                                chart.renderer.rect(0, H - 57, W, 1)
                                    .attr({ fill: '#a3d237', zIndex: 5 })
                                    .add();

                                chart.renderer.rect(0, H - 58, 6, 58)
                                    .attr({ fill: '#007b4e', zIndex: 6 })
                                    .add();

                                chart.renderer.text(
                                    `Generated ${moment().format("MMM DD, YYYY [at] h:mm A")}`,
                                    22, H - 5
                                )
                                    .attr({ align: 'left', zIndex: 6 })
                                    .css({ color: '#aaaaaa', fontSize: '10px' })
                                    .add();

                                chart.renderer.text('CONFIDENTIAL  ·  FOR INTERNAL USE ONLY', W / 2, H - 5)
                                    .attr({ align: 'center', zIndex: 6 })
                                    .css({
                                        color: '#cccccc',
                                        fontSize: '9px',
                                        fontWeight: '700',
                                        letterSpacing: '0.12em',
                                    })
                                    .add();

                                chart.renderer.text(`© ${new Date().getFullYear()} All rights reserved`, W - 22, H - 5)
                                    .attr({ align: 'right', zIndex: 6 })
                                    .css({ color: '#aaaaaa', fontSize: '10px' })
                                    .add();
                            }
                        }
                    }
                }
            },
        })

        setLoading(false)
    }, [currentData, props.buildings, unitDict, displayOptions, displayedUtilities, temperatureUnit, unit, euiTotalPerMonth, euiTotalPerMonthC, euiNational, euiNationalC, timeStep, maxEui, cTotals, compareData, compareStartEnd, props.startEnd, selectedBuilding?.buildingInfo?.building, totals])

    const handleChangeUnit = (e) => {
        if (e.currentTarget.value === 'consumption') setDisplayedUtilities([2])
        setUnit(e.currentTarget.value)
    }

    const handleSelectedUtility = (e) => {
        let val = e.target.value

        if (unit === "consumption") {
            setDisplayedUtilities(prev => prev.includes(val) ? [] : [val])
            return
        }

        if (val === 'all') {
            setDisplayedUtilities(utilityOptions.map(e => e.idAttr))
            return
        }

        if (displayedUtilities.includes(val)) {
            setDisplayedUtilities(prev => prev.filter(v => v !== val))
        } else {
            setDisplayedUtilities(prev => [...prev, val])
        }
    }

    const handleSelectedTag = (e) => {
        let val = e.target?.value
        if (!val) return
        if (val === 'all') {
            setDisplayedTags(tagOptions.map(e => e.idAttr))
            return
        }
        if (displayedTags.includes(val)) {
            setDisplayedTags(prev => prev.filter(v => v !== val))
        } else {
            setDisplayedTags(prev => [...prev, val])
        }
    }


    useEffect(() => {
        handleSelectedTag(andOr)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [andOr])

    // Derive display values for the selects (show pending if set, otherwise applied)
    const displayedBuildingId = (pendingBuilding ?? selectedBuilding)?.buildingInfo.building_id
    const displayedCompareBuilding = pendingCompareBuilding !== undefined ? (pendingCompareBuilding ?? "") : (compareBuilding ?? "")
    const displayedCompareStartEnd = pendingCompareStartEnd !== undefined ? pendingCompareStartEnd : compareStartEnd

    return (
        <div>
            <div className={`card tablet:p-8 mobile:p-4 rounded-lg relative mt-8`}>
                <div className='flex flex-nowrap justify-between items-center'>
                    <div>
                        <h2 className="capitalize w-full laptop:text-3xl tablet:text-xl   font-bold text-[#332D41] md:decoration-dashed">Building Analysis Graph</h2>
                        {props.startEnd && <p className='mb-4 font-light text-sm'>Date Range: <span className='font-normal'>{moment(props.startEnd[0]).format("MMM DD, YYYY")} - {moment(props.startEnd[1]).format("MMM DD, YYYY")} ({GraphFuncs.getDifferenceInDays(props.startEnd[0], props.startEnd[1])} days)</span></p>}
                        {props.compare && props.compareStartEnd.length === 2 && <p className='mb-4 font-light text-sm'>Comparative Range: <span className='font-normal'>{moment(props.compareStartEnd[0]).format("MMM DD, YYYY")} - {moment(props.compareStartEnd[1]).format("MMM DD, YYYY")} ({GraphFuncs.getDifferenceInDays(props.compareStartEnd[0], props.compareStartEnd[1])} days)</span></p>}
                    </div>
                </div>
                <div className='flex flex-nowrap items-center justify-start gap-4 mb-5'>
                    <p className='text-[14px]'>Viewing</p>
                    <div className="flex flex-nowrap justify-center items-center">
                        <select className='w-1/2 p-2 border border-1 rounded  border-[#C1C1C1] text-[#007b4e] w-[220px]' value={displayedBuildingId} onChange={handleSelectedBuilding}>
                            {
                                buildingOptions && buildingOptions.map((e, ix) => (
                                    <option key={"buildings" + ix} value={e.buildingId}>{e.name}</option>
                                ))
                            }
                        </select>
                    </div>
                    <p className='text-[14px]'>Comparing with</p>
                    <div className="flex flex-nowrap justify-center items-center">
                        <select className='w-1/2 p-2 border border-1 rounded  border-[#C1C1C1] text-[#007b4e] w-[220px]' value={displayedCompareBuilding} onChange={handleCompareBuilding}>
                            <option value="">None</option>
                            {
                                buildingOptions && buildingOptions.map((e, ix) => (
                                    <option key={"buildings" + ix} value={e.buildingId}>{e.name}</option>
                                ))
                            }
                        </select>
                    </div>
                    <p className='text-[14px]'>Or</p>
                    <div className='px-1 control-pane mx-2 font-sans text-[#007b4e] border border-solid border-[#007b4e] rounded-lg text-sm leading-4 font-bold'>
                        <DatePickerComponent start="Year" depth="Year" format="MMM yyyy" onChange={handleDateChange} value={displayedCompareStartEnd ? moment(displayedCompareStartEnd[0]).toDate() : null} placeholder='Start Month' />
                    </div>
                    <div className='flex justify-end items-center m-2'>
                        <p className="text-[#332D41] md:decoration-dashed text-[14px]">Interval:</p>
                        <select className='w-1/2 p-2 ml-2 text-sm text-[#007b4e]' name="eDetail" value={timeStep} onChange={(e) => setTimeStep(e.target.value)}>
                            <option value="0">Month</option>
                            <option value="1">Quarter</option>
                            <option value="2">Year</option>
                        </select>
                    </div>
                    {showUpdate && <button className='submit-button-small' onClick={handleCompare}>
                        Update
                    </button>}
                    <button className='text-[#007b4e]' onClick={reset}>
                        Reset
                    </button>
                </div>
                <div className="flex flex-nowrap items-start justify-start gap-10 w-full">
                    <div className="flex flex-nowrap items-start justify-start gap-10 w-full">
                        <div>
                            <h2 className='mb-2 font-medium'>Grand Total: {unit === 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{(displayedUtilities.reduce((total, item) => {
                                return total += totals[unit][item]
                            }, 0)).toLocaleString(undefined,
                                displayOptions.monthsqft
                                    ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                    : { maximumFractionDigits: 0 })} {unit !== 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{displayOptions.monthsqft ? " /m²" : ""}</h2>
                            {!['eui', 'consumption'].includes(unit) && <div className='flex flex-nowrap items-center'>
                                Electrical: {unit === 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{(totals[unit][0]).toLocaleString(undefined,
                                    displayOptions.monthsqft
                                        ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                        : { maximumFractionDigits: 0 })} {unit !== 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{displayOptions.monthsqft ? " /m²" : ""}
                                {unit === 'energy' &&
                                    <Tooltip
                                        text="1 GJ = 277.7778 kWh"
                                        right="0"
                                        left="400%"
                                        width="300px"
                                        show={true}>
                                        <div className="ml-2">
                                            {GlobalSVG.info('black', '15', '15')}
                                        </div>
                                    </Tooltip>}
                            </div>}
                            {!['eui', 'consumption'].includes(unit) && <div className='flex flex-nowrap items-center'>
                                Natural Gas: {unit === 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{(totals[unit][1]).toLocaleString(undefined,
                                    displayOptions.monthsqft
                                        ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                        : { maximumFractionDigits: 0 })} {unit !== 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{displayOptions.monthsqft ? " /m²" : ""}
                                {unit === 'energy' &&
                                    <Tooltip
                                        text="1 GJ = 947.81714 cubic feet of natural gas<br />1 GJ = 26.853 cubic metres of natural gas"
                                        right="0"
                                        left="400%"
                                        width="300px"
                                        show={true}>
                                        <div className="ml-2">
                                            {GlobalSVG.info('black', '15', '15')}
                                        </div>
                                    </Tooltip>}
                            </div>}
                            {!['eui', 'energy', 'emissions', 'consumption'].includes(unit) && <div className='flex flex-nowrap items-center'>
                                Water: {unit === 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{(totals[unit][2]).toLocaleString(undefined,
                                    displayOptions.monthsqft
                                        ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                        : { maximumFractionDigits: 0 })} {unit !== 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{displayOptions.monthsqft ? " /m²" : ""}
                            </div>}
                        </div>
                        {compareData && <div>
                            <h2 className='mb-2 font-medium'>Grand Total (C): {unit === 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{(displayedUtilities.reduce((total, item) => {
                                return total += cTotals[unit][item]
                            }, 0)).toLocaleString(undefined,
                                displayOptions.monthsqft
                                    ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                    : { maximumFractionDigits: 0 })} {unit !== 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{displayOptions.monthsqft ? " /m²" : ""}</h2>
                            {!['eui', 'consumption'].includes(unit) && <div className='flex flex-nowrap items-center'>
                                Electrical (C): {unit === 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{(cTotals[unit][0]).toLocaleString(undefined,
                                    displayOptions.monthsqft
                                        ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                        : { maximumFractionDigits: 0 })} {unit !== 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{displayOptions.monthsqft ? " /m²" : ""}
                                {unit === 'energy' &&
                                    <Tooltip
                                        text="1 GJ = 277.7778 kWh"
                                        right="0"
                                        left="400%"
                                        width="300px"
                                        show={true}>
                                        <div className="ml-2">
                                            {GlobalSVG.info('black', '15', '15')}
                                        </div>
                                    </Tooltip>}
                            </div>}
                            {!['eui', 'consumption'].includes(unit) && <div className='flex flex-nowrap items-center'>
                                Natural Gas (C): {unit === 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{(cTotals[unit][1]).toLocaleString(undefined,
                                    displayOptions.monthsqft
                                        ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                        : { maximumFractionDigits: 0 })} {unit !== 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{displayOptions.monthsqft ? " /m²" : ""}
                                {unit === 'energy' &&
                                    <Tooltip
                                        text="1 GJ = 947.81714 cubic feet of natural gas<br />1 GJ = 26.853 cubic metres of natural gas"
                                        right="0"
                                        left="400%"
                                        width="300px"
                                        show={true}>
                                        <div className="ml-2">
                                            {GlobalSVG.info('black', '15', '15')}
                                        </div>
                                    </Tooltip>}
                            </div>}
                            {!['eui', 'energy', 'emissions', 'consumption'].includes(unit) && <div className='flex flex-nowrap items-center'>
                                Water (C): {unit === 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{(cTotals[unit][2]).toLocaleString(undefined,
                                    displayOptions.monthsqft
                                        ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                        : { maximumFractionDigits: 0 })} {unit !== 'price' ? unitDict[unit][displayedUtilities[0]] : ""}{displayOptions.monthsqft ? " /m²" : ""}
                            </div>}
                        </div>
                        }
                    </div>
                    <div>
                        <div className="flex flex-nowrap justify-center items-center mb-1">
                            <select className='w-1/2 p-2 border border-1 rounded  border-[#C1C1C1] text-[#007b4e] w-[220px]' value={unit} onChange={handleChangeUnit}>
                                <option value="price">Cost ($)</option>
                                <option value="energy">Energy (GJ)</option>
                                <option value="emissions">Emissions (t CO₂e)</option>
                                <option value="consumption">Consumption</option>
                            </select>
                            {displayOptions.monthsqft &&
                                <Tooltip
                                    text="Buildings with no square footage info will not show values"
                                    right="0"
                                    bottom="100%"
                                    left="-500%"
                                    width="200px"
                                    show={true}>
                                    <div className="ml-2">
                                        {GlobalSVG.info('black', '15', '15')}
                                    </div>
                                </Tooltip>}
                        </div>
                        <MultipleSelect reports={true} data={utilityOptions} dataType='buildingId' idAttr="idAttr" valAttr="utility" selectItem={handleSelectedUtility} selectedList={displayedUtilities} placeholder="Select Utilities" />
                        <MultipleSelect reports={true} data={tagOptions} idAttr="idAttr" valAttr="tag" selectItem={handleSelectedTag} selectedList={displayedTags} placeholder="Select Tags" />
                        <UnitToggle setOptions={setAndOr} options={andOr} option1="and" option2="or" button1="And" button2="Or" info='Joining tags by "And" vs "Or"' />
                        <SlideToggle setOptions={setDisplayOptions} disabled={monthsBetween(props.startEnd) < 12} titleClass={monthsBetween(props.startEnd) < 12 ? "text-[grey]" : ''} info={monthsBetween(props.startEnd) < 12 ? "Selected period must be equal to or greater than 12 months." : ""} options={displayOptions} id={"montheui"} name={"EUI"} />
                        {selectedBuilding && selectedBuilding.buildingInfo.sq_footage && <SlideToggle setOptions={setDisplayOptions} options={displayOptions} id={"monthsqft"} name={"Per Square Metre"} />}
                        <SlideToggle setOptions={setDisplayOptions} options={displayOptions} id={"temp"} name={"Temperature"} />
                        {displayOptions.temp && <UnitToggle setOptions={setTemperatureUnit} options={temperatureUnit} option1="c" option2="f" button1="&deg;C" button2="&deg;F" />}
                    </div>
                </div>
                <div className="flex flex-nowrap tablet:justify-end laptop:justify-start mobile:justify-end items-center rounded-lg min-h-[600px]">
                    <div className={`col-span-2 w-full`}>
                        {options && <HighchartsReact
                            highcharts={Highcharts}
                            options={options}
                            ref={chartRef}
                            immutable={true}
                        />}
                        {loading && <Loader />}
                    </div>
                </div>
            </div>
        </div>
    )
}